import { escapeHtml } from "./escapeHtml.js";

export type MaxGraphEdge = { from: string; to: string };
export type MaxGraphData = { edges: MaxGraphEdge[] };
/** Named graphs accumulated across `##name=edges##` blocks; mutated in document order like `MaxCalcTables`. */
export type MaxGraphs = Record<string, MaxGraphData>;
/** Node-id → CSS color, accumulated by `##color{X}{a,b,c}##` directives. */
export type MaxNodeColors = Record<string, string>;
export type MaxGraphOrientation = "vertical" | "horizontal";
/** Graph name → layout orientation; defaults to `vertical` when missing. */
export type MaxGraphOrientations = Record<string, MaxGraphOrientation>;

const NODE_R = 22;
const RANK_GAP = 80;
const NODE_GAP = 60;
const PADDING = 32;
const ARC_CLEARANCE = 60;
const EDGE_LANE_STEP = 10;

/** Curated palette: nicer versions of common color names; falls back to the raw string. */
const COLOR_PALETTE: Record<string, string> = {
  red:    "#cc3333",
  blue:   "#3366cc",
  green:  "#2e9e50",
  orange: "#d96820",
  purple: "#7744bb",
  teal:   "#1e9999",
  yellow: "#b89020",
  pink:   "#cc3388",
  cyan:   "#1a99bb",
  gray:   "#888888",
  grey:   "#888888",
};

export function resolveGraphColor(name: string): string {
  return COLOR_PALETTE[name.toLowerCase()] ?? name;
}

/** Apply `color` to every node id in `nodeList` (comma-separated string), storing in `nodeColors`. */
export function applyMaxNodeColors(color: string, nodeList: string, nodeColors: MaxNodeColors): void {
  const resolved = resolveGraphColor(color);
  for (const id of nodeList.split(",").map((s) => s.trim()).filter(Boolean)) {
    nodeColors[id] = resolved;
  }
}

/** Apply `vertical|horizontal` orientation to every graph name in `graphList` (comma-separated). */
export function applyMaxGraphOrientation(
  orientation: MaxGraphOrientation,
  graphList: string,
  graphOrientations: MaxGraphOrientations,
): void {
  for (const name of graphList.split(",").map((s) => s.trim()).filter(Boolean)) {
    if (/^[a-zA-Z][a-zA-Z0-9]*$/.test(name)) {
      graphOrientations[name] = orientation;
    }
  }
}

/** Parse a `->` chain like `a->b->c->a` into sequential directed edges. */
export function parseMaxGraphEdgeChain(chain: string): MaxGraphEdge[] {
  const parts = chain.split("->").map((s) => s.trim()).filter(Boolean);
  const edges: MaxGraphEdge[] = [];
  for (let i = 0; i + 1 < parts.length; i++) {
    edges.push({ from: parts[i]!, to: parts[i + 1]! });
  }
  return edges;
}

/**
 * Parse `edgesStr` (a single `->` chain) and accumulate into `graphs[name]`.
 * Duplicate edges are silently ignored; nodes are shared across calls with the same name.
 */
export function accumulateMaxGraphEdges(name: string, edgesStr: string, graphs: MaxGraphs): void {
  if (!graphs[name]) graphs[name] = { edges: [] };
  const newEdges = parseMaxGraphEdgeChain(edgesStr);
  const existing = graphs[name]!.edges;
  for (const e of newEdges) {
    if (!existing.some((ex) => ex.from === e.from && ex.to === e.to)) {
      existing.push(e);
    }
  }
}

interface LayoutNode {
  id: string;
  cx: number;
  cy: number;
}

interface LayoutEdge {
  from: string;
  to: string;
  isBack: boolean;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
}

function isLongForwardEdge(e: LayoutEdge): boolean {
  const verticalSpan = e.ty - e.sy;
  const colAligned = Math.abs(e.tx - e.sx) < 1e-6;
  return !e.isBack && colAligned && verticalSpan > NODE_R * 2 + RANK_GAP;
}

function forwardEdgeVisualStyle(
  siblingIndex: number,
  siblingCount: number,
): { strokeWidth: number; strokeOpacity: number } {
  if (siblingCount <= 1) return { strokeWidth: 1.6, strokeOpacity: 0.98 };
  const center = (siblingCount - 1) / 2;
  const dist = Math.abs(siblingIndex - center);
  const norm = center > 0 ? dist / center : 0;
  return {
    strokeWidth: 1.7 - 0.35 * norm,
    strokeOpacity: 0.98 - 0.3 * norm,
  };
}

function edgeControlPoint(
  e: LayoutEdge,
  bend: number,
): { mx: number; my: number; nx: number; ny: number; len: number } {
  const dx = e.tx - e.sx;
  const dy = e.ty - e.sy;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const mx = (e.sx + e.tx) / 2 + nx * bend;
  const my = (e.sy + e.ty) / 2 + ny * bend;
  return { mx, my, nx, ny, len };
}

function controlPointCrowdingScore(
  e: LayoutEdge,
  nodes: LayoutNode[],
  bend: number,
): number {
  const { mx, my } = edgeControlPoint(e, bend);
  let score = 0;
  for (const n of nodes) {
    if (n.id === e.from || n.id === e.to) continue;
    const d = Math.hypot(n.cx - mx, n.cy - my);
    if (d < NODE_R * 2.2) score += NODE_R * 2.2 - d;
  }
  return score;
}

function chooseBendSign(
  e: LayoutEdge,
  nodes: LayoutNode[],
): number {
  const avgX = nodes.reduce((acc, n) => acc + n.cx, 0) / Math.max(1, nodes.length);
  const midpointX = (e.sx + e.tx) / 2;
  const nearCenter = midpointX >= avgX ? 1 : -1;
  const test = 24;
  const pos = controlPointCrowdingScore(e, nodes, +test);
  const neg = controlPointCrowdingScore(e, nodes, -test);
  if (Math.abs(pos - neg) < 0.01) return nearCenter;
  return pos <= neg ? 1 : -1;
}

function forwardEdgePath(
  e: LayoutEdge,
  nodes: LayoutNode[],
  siblingIndex: number,
  siblingCount: number,
): string | null {
  const dy = e.ty - e.sy;
  const lane = siblingIndex - (siblingCount - 1) / 2;
  const isSkip = isLongForwardEdge(e) || Math.abs(dy) > NODE_R * 2 + RANK_GAP;
  if (!isSkip && siblingCount <= 1) return null;
  const dir = chooseBendSign(e, nodes);
  const bend = dir * ((isSkip ? 30 : 14) + Math.abs(lane) * EDGE_LANE_STEP);
  const { mx, my } = edgeControlPoint(e, bend);
  return `M${e.sx},${e.sy} Q${mx.toFixed(2)},${my.toFixed(2)} ${e.tx},${e.ty}`;
}

function layoutGraph(data: MaxGraphData, orientation: MaxGraphOrientation = "vertical"): {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  w: number;
  h: number;
} {
  // Collect unique node ids in order of first appearance.
  const nodeOrder: string[] = [];
  const seen = new Set<string>();
  for (const e of data.edges) {
    if (!seen.has(e.from)) { nodeOrder.push(e.from); seen.add(e.from); }
    if (!seen.has(e.to)) { nodeOrder.push(e.to); seen.add(e.to); }
  }

  const adj = new Map<string, Set<string>>();
  for (const id of nodeOrder) adj.set(id, new Set());
  for (const e of data.edges) adj.get(e.from)!.add(e.to);

  // DFS to detect back-edges (cycle detection).
  const color = new Map<string, "white" | "gray" | "black">();
  for (const id of nodeOrder) color.set(id, "white");
  const backSet = new Set<string>();

  function dfs(id: string) {
    color.set(id, "gray");
    for (const next of adj.get(id) ?? []) {
      const key = `${id}\x00${next}`;
      if (color.get(next) === "gray") {
        backSet.add(key);
      } else if (color.get(next) === "white") {
        dfs(next);
      }
    }
    color.set(id, "black");
  }
  for (const id of nodeOrder) if (color.get(id) === "white") dfs(id);

  // Assign ranks: longest forward path from any source node.
  const rank = new Map<string, number>();
  for (const id of nodeOrder) rank.set(id, 0);
  // Iterative relaxation (handles diamonds and multiple paths).
  for (let pass = 0; pass < nodeOrder.length; pass++) {
    for (const e of data.edges) {
      if (backSet.has(`${e.from}\x00${e.to}`)) continue;
      const proposed = (rank.get(e.from) ?? 0) + 1;
      if (proposed > (rank.get(e.to) ?? 0)) rank.set(e.to, proposed);
    }
  }

  // Group nodes by rank.
  const rankGroups = new Map<number, string[]>();
  for (const id of nodeOrder) {
    const r = rank.get(id)!;
    if (!rankGroups.has(r)) rankGroups.set(r, []);
    rankGroups.get(r)!.push(id);
  }
  const maxRank = Math.max(...Array.from(rank.values()));
  const maxRowLen = Math.max(...Array.from(rankGroups.values()).map((g) => g.length));

  const diameter = NODE_R * 2;
  const totalW = PADDING * 2 + maxRowLen * diameter + (maxRowLen - 1) * NODE_GAP + ARC_CLEARANCE * 2;
  const totalH = PADDING * 2 + (maxRank + 1) * diameter + maxRank * RANK_GAP;

  const pos = new Map<string, { cx: number; cy: number }>();
  for (const [r, ids] of rankGroups) {
    if (orientation === "horizontal") {
      const colH = ids.length * diameter + (ids.length - 1) * NODE_GAP;
      const startY = (totalW - colH) / 2;
      for (let i = 0; i < ids.length; i++) {
        pos.set(ids[i]!, {
          cx: PADDING + r * (diameter + RANK_GAP) + NODE_R,
          cy: startY + i * (diameter + NODE_GAP) + NODE_R,
        });
      }
    } else {
      const rowW = ids.length * diameter + (ids.length - 1) * NODE_GAP;
      const startX = (totalW - rowW) / 2;
      for (let i = 0; i < ids.length; i++) {
        pos.set(ids[i]!, {
          cx: startX + i * (diameter + NODE_GAP) + NODE_R,
          cy: PADDING + r * (diameter + RANK_GAP) + NODE_R,
        });
      }
    }
  }

  const nodes: LayoutNode[] = nodeOrder.map((id) => ({ id, ...pos.get(id)! }));

  const edges: LayoutEdge[] = data.edges.map((e) => {
    const isBack = backSet.has(`${e.from}\x00${e.to}`);
    const sp = pos.get(e.from)!;
    const tp = pos.get(e.to)!;
    let sx: number, sy: number, tx: number, ty: number;
    if (isBack) {
      // Back-edge leaves from the "outer" side and curves around.
      if (orientation === "horizontal") {
        sx = sp.cx;
        sy = sp.cy - NODE_R;
        tx = tp.cx;
        ty = tp.cy - NODE_R;
      } else {
        sx = sp.cx - NODE_R;
        sy = sp.cy;
        tx = tp.cx - NODE_R;
        ty = tp.cy;
      }
    } else if (orientation === "horizontal") {
      // Forward: right of source to left of target.
      sx = sp.cx + NODE_R;
      sy = sp.cy;
      tx = tp.cx - NODE_R;
      ty = tp.cy;
    } else {
      // Forward: bottom of source to top of target.
      sx = sp.cx;
      sy = sp.cy + NODE_R;
      tx = tp.cx;
      ty = tp.cy - NODE_R;
    }
    return { from: e.from, to: e.to, isBack, sx, sy, tx, ty };
  });
  if (orientation === "horizontal") {
    return { nodes, edges, w: totalH, h: totalW };
  }
  return { nodes, edges, w: totalW, h: totalH };
}

/** Render named graph `graphName` as a fenced Mermaid `graph TD` code block (Markdown format). */
export function buildMaxGraphMermaid(graphName: string, graphs: MaxGraphs): string {
  const data = graphs[graphName];
  if (!data || data.edges.length === 0) {
    return `\`\`\`mermaid\ngraph TD\n  %% graph ${graphName}: no edges defined\n\`\`\``;
  }
  const lines: string[] = ["graph TD"];
  for (const e of data.edges) {
    lines.push(`    ${e.from} --> ${e.to}`);
  }
  return "```mermaid\n" + lines.join("\n") + "\n```";
}

/** Render named graph `graphName` as a fenced Mermaid block wrapped in a `<div class="npp-max-graph-md">` for preview display. */
export function buildMaxGraphMermaidHtml(graphName: string, graphs: MaxGraphs): string {
  const data = graphs[graphName];
  const lines: string[] = ["graph TD"];
  if (data && data.edges.length > 0) {
    for (const e of data.edges) {
      lines.push(`    ${escapeHtml(e.from)} --> ${escapeHtml(e.to)}`);
    }
  } else {
    lines.push(`    %% graph ${escapeHtml(graphName)}: no edges defined`);
  }
  const code = lines.join("\n");
  return (
    `<div class="npp-max-graph-md">` +
    `<pre class="npp-max-graph-md-pre"><code class="language-mermaid">${code}</code></pre>` +
    `</div>`
  );
}
export function buildMaxGraphSvg(
  graphName: string,
  graphs: MaxGraphs,
  nodeColors: MaxNodeColors = {},
  orientation: MaxGraphOrientation = "vertical",
): string {
  const data = graphs[graphName];
  if (!data || data.edges.length === 0) {
    return `<div class="npp-max-graph npp-max-graph--empty">(graph <code>${escapeHtml(graphName)}</code>: no edges defined)</div>`;
  }

  const { nodes, edges, w, h } = layoutGraph(data, orientation);

  // Use a stable per-render id prefix so multiple graphs on one page don't share marker ids.
  const uid = `mg-${graphName}`;

  const cycleNodeIds = new Set<string>();
  for (const e of edges) {
    if (e.isBack) { cycleNodeIds.add(e.from); cycleNodeIds.add(e.to); }
  }

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" class="npp-max-graph-svg" aria-label="graph ${escapeHtml(graphName)}">`,
  );
  parts.push(`<defs>`);
  // Forward edge arrowhead (inherits currentColor).
  parts.push(
    `<marker id="${uid}-af" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">` +
    `<path d="M0,0 L7,3.5 L0,7 Z" fill="currentColor"/>` +
    `</marker>`,
  );
  // Back-edge arrowhead (gray).
  parts.push(
    `<marker id="${uid}-ab" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">` +
    `<path d="M0,0 L7,3.5 L0,7 Z" fill="#999"/>` +
    `</marker>`,
  );
  parts.push(`</defs>`);

  // Edges — drawn before nodes so nodes appear on top.
  const forwardGeomCount = new Map<string, number>();
  const forwardGeomSeen = new Map<string, number>();
  for (const e of edges) {
    if (e.isBack) continue;
    const k = `${e.sx.toFixed(2)},${e.sy.toFixed(2)}->${e.tx.toFixed(2)},${e.ty.toFixed(2)}`;
    forwardGeomCount.set(k, (forwardGeomCount.get(k) ?? 0) + 1);
  }
  for (const e of edges) {
    if (e.isBack) {
      if (orientation === "horizontal") {
        const ly = Math.min(e.sy, e.ty) - ARC_CLEARANCE;
        const d = `M${e.sx},${e.sy} C${e.sx},${ly} ${e.tx},${ly} ${e.tx},${e.ty}`;
        parts.push(
          `<path d="${d}" fill="none" stroke="#999" stroke-width="1.5" stroke-dasharray="5,3" marker-end="url(#${uid}-ab)"/>`,
        );
      } else {
        const lx = Math.min(e.sx, e.tx) - ARC_CLEARANCE;
        const d = `M${e.sx},${e.sy} C${lx},${e.sy} ${lx},${e.ty} ${e.tx},${e.ty}`;
        parts.push(
          `<path d="${d}" fill="none" stroke="#999" stroke-width="1.5" stroke-dasharray="5,3" marker-end="url(#${uid}-ab)"/>`,
        );
      }
    } else {
      const k = `${e.sx.toFixed(2)},${e.sy.toFixed(2)}->${e.tx.toFixed(2)},${e.ty.toFixed(2)}`;
      const count = forwardGeomCount.get(k) ?? 1;
      const idx = forwardGeomSeen.get(k) ?? 0;
      forwardGeomSeen.set(k, idx + 1);
      const vis = forwardEdgeVisualStyle(idx, count);
      const curved = forwardEdgePath(e, nodes, idx, count);
      if (curved) {
        parts.push(
          `<path d="${curved}" fill="none" stroke="currentColor" stroke-width="${vis.strokeWidth.toFixed(2)}" stroke-opacity="${vis.strokeOpacity.toFixed(2)}" marker-end="url(#${uid}-af)"/>`,
        );
      } else {
        parts.push(
          `<line x1="${e.sx}" y1="${e.sy}" x2="${e.tx}" y2="${e.ty}" stroke="currentColor" stroke-width="${vis.strokeWidth.toFixed(2)}" stroke-opacity="${vis.strokeOpacity.toFixed(2)}" marker-end="url(#${uid}-af)"/>`,
        );
      }
    }
  }

  // Nodes.
  for (const n of nodes) {
    const inCycle = cycleNodeIds.has(n.id);
    const userColor = nodeColors[n.id];
    if (userColor) {
      // Colored node: tinted fill + full-opacity stroke in the user color, white label.
      parts.push(
        `<circle cx="${n.cx}" cy="${n.cy}" r="${NODE_R}" fill="${userColor}" fill-opacity="0.18" stroke="${userColor}" stroke-width="2"/>`,
      );
      parts.push(
        `<text x="${n.cx}" y="${n.cy}" text-anchor="middle" dominant-baseline="central" ` +
        `style="font-size:14px;font-family:monospace;font-weight:600;fill:${userColor}">${escapeHtml(n.id)}</text>`,
      );
    } else {
      const strokeW = inCycle ? 2 : 1.5;
      const strokeColor = inCycle ? "currentColor" : "#bbb";
      parts.push(
        `<circle cx="${n.cx}" cy="${n.cy}" r="${NODE_R}" fill="#fff" stroke="${strokeColor}" stroke-width="${strokeW}"/>`,
      );
      parts.push(
        `<text x="${n.cx}" y="${n.cy}" text-anchor="middle" dominant-baseline="central" ` +
        `style="font-size:14px;font-family:monospace;font-weight:600;fill:currentColor">${escapeHtml(n.id)}</text>`,
      );
    }
  }

  parts.push(`</svg>`);
  return `<div class="npp-max-graph">${parts.join("")}</div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// ASCII box-art renderer
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Render the named graph as ASCII-art rectangular boxes with connecting arrows.
/**
 * Generate the plain-text ASCII box diagram for `name` (no HTML wrapping).
 * Shared by the HTML preview (`buildMaxGraphAscii`) and the markdown export (`buildMaxGraphAsciiText`).
 *
 * orientation="horizontal"  → rank flows left→right, nodes within a rank stack top→bottom.
 *   Connectors: ────▶ (same row), L-shaped (adjacent col, different row), ∩-arcs above (back-edges).
 *
 * orientation="vertical" (default, matching SVG default)
 *   → rank flows top→bottom, nodes within a rank sit left→right.
 *   Connectors: │ ▼ (same col), L-shaped (adjacent rank, different col), ╗-arcs to the right (back-edges).
 */
function buildMaxGraphAsciiRaw(
  name: string,
  maxGraphs: MaxGraphs,
  orientation: MaxGraphOrientation = "vertical",
): string {
  const data = maxGraphs[name];
  if (!data || data.edges.length === 0) {
    return `(graph "${name}" \u2014 no edges defined)`;
  }

  // ── 1. Collect nodes & adjacency ──────────────────────────────────────────
  const nodeOrder: string[] = [];
  const nodeSet = new Set<string>();
  for (const { from, to } of data.edges) {
    if (!nodeSet.has(from)) { nodeOrder.push(from); nodeSet.add(from); }
    if (!nodeSet.has(to))   { nodeOrder.push(to);   nodeSet.add(to);   }
  }
  const adj = new Map<string, Set<string>>();
  for (const id of nodeOrder) adj.set(id, new Set());
  for (const { from, to } of data.edges) adj.get(from)!.add(to);

  // ── 2. DFS back-edge detection ────────────────────────────────────────────
  const dfsColor = new Map<string, 0 | 1 | 2>(); // 0=unvisited 1=in-stack 2=done
  for (const id of nodeOrder) dfsColor.set(id, 0);
  const backSet = new Set<string>();
  function dfsVisit(id: string): void {
    dfsColor.set(id, 1);
    for (const nx of adj.get(id) ?? []) {
      const key = `${id}\0${nx}`;
      if (dfsColor.get(nx) === 1) backSet.add(key);
      else if (dfsColor.get(nx) === 0) dfsVisit(nx);
    }
    dfsColor.set(id, 2);
  }
  for (const id of nodeOrder) if (dfsColor.get(id) === 0) dfsVisit(id);

  // ── 3. Rank = longest forward-path depth ──────────────────────────────────
  const rank = new Map<string, number>();
  for (const id of nodeOrder) rank.set(id, 0);
  for (let pass = 0; pass < nodeOrder.length; pass++) {
    for (const { from, to } of data.edges) {
      if (backSet.has(`${from}\0${to}`)) continue;
      const p = (rank.get(from) ?? 0) + 1;
      if (p > (rank.get(to) ?? 0)) rank.set(to, p);
    }
  }

  // ── 4. Group nodes by rank ─────────────────────────────────────────────────
  const rankGroups = new Map<number, string[]>();
  for (const id of nodeOrder) {
    const r = rank.get(id)!;
    if (!rankGroups.has(r)) rankGroups.set(r, []);
    rankGroups.get(r)!.push(id);
  }

  const selfLoops   = data.edges.filter(({ from, to }) => from === to && backSet.has(`${from}\0${to}`));
  const backEdgeList = data.edges.filter(({ from, to }) => backSet.has(`${from}\0${to}`) && from !== to);

  // ── 5+ Layout ─────────────────────────────────────────────────────────────
  if (orientation === "horizontal") {
    return buildMaxGraphAsciiHorizontal(nodeOrder, data, backSet, rank, rankGroups, backEdgeList, selfLoops);
  }
  return buildMaxGraphAsciiVertical(nodeOrder, data, backSet, rank, rankGroups, backEdgeList, selfLoops);
}

// ─────────────────────────────────────────────────────────────────────────────
// Horizontal layout (rank = column, left → right)
// ─────────────────────────────────────────────────────────────────────────────
function buildMaxGraphAsciiHorizontal(
  nodeOrder: string[],
  data: MaxGraphData,
  backSet: Set<string>,
  rank: Map<string, number>,
  rankGroups: Map<number, string[]>,
  backEdgeList: MaxGraphEdge[],
  selfLoops: MaxGraphEdge[],
): string {
  // col = rank, row = position within rank
  const nodeCol = new Map<string, number>();
  const nodeRow = new Map<string, number>();
  for (const [r, ids] of rankGroups) {
    for (let i = 0; i < ids.length; i++) {
      nodeCol.set(ids[i]!, r);
      nodeRow.set(ids[i]!, i);
    }
  }

  const numCols = rank.size > 0 ? Math.max(...rank.values()) + 1 : 1;
  const numRows = rankGroups.size > 0
    ? Math.max(...Array.from(rankGroups.values(), (g) => g.length))
    : 1;

  const colW = new Array<number>(numCols).fill(5);
  for (const id of nodeOrder) {
    const c = nodeCol.get(id)!;
    colW[c] = Math.max(colW[c]!, id.length + 4);
  }

  const H_GAP = 6;
  const ROW_H = 5; // box height (3) + routing gap (2)

  const colX = new Array<number>(numCols).fill(0);
  let xAcc = 0;
  for (let c = 0; c < numCols; c++) { colX[c] = xAcc; xAcc += colW[c]! + H_GAP; }
  const totalW = xAcc - H_GAP;

  const numLanes  = Math.min(backEdgeList.length, 4);
  const TOP_MARGIN = numLanes > 0 ? numLanes + 2 : 0;
  const totalH    = (numRows > 1 ? numRows * ROW_H - 2 : 3) + TOP_MARGIN;

  const grid: string[][] = Array.from({ length: totalH }, () => new Array<string>(totalW).fill(" "));
  const gset  = (x: number, y: number, ch: string)                        => { if (x >= 0 && x < totalW && y >= 0 && y < totalH) grid[y]![x] = ch; };
  const hLine = (x1: number, x2: number, y: number, ch = "─")            => { for (let x = x1; x <= x2; x++) gset(x, y, ch); };
  const vLine = (y1: number, y2: number, x: number, ch = "│")            => { for (let y = y1; y <= y2; y++) gset(x, y, ch); };

  // Paint boxes
  for (const id of nodeOrder) {
    const c = nodeCol.get(id)!;
    const r = nodeRow.get(id)!;
    const bx = colX[c]!;
    const by = r * ROW_H + TOP_MARGIN;
    const w  = colW[c]!;
    const inner = w - 2;
    gset(bx, by, "┌"); hLine(bx + 1, bx + w - 2, by); gset(bx + w - 1, by, "┐");
    gset(bx, by + 1, "│");
    const pad = Math.floor((inner - id.length) / 2);
    for (let i = 0; i < inner; i++) gset(bx + 1 + i, by + 1, " ");
    for (let i = 0; i < id.length; i++) gset(bx + 1 + pad + i, by + 1, id[i]!);
    gset(bx + w - 1, by + 1, "│");
    gset(bx, by + 2, "└"); hLine(bx + 1, bx + w - 2, by + 2); gset(bx + w - 1, by + 2, "┘");
  }

  // Back-edge arcs above (∩-shape)
  backEdgeList.slice(0, numLanes).forEach(({ from, to }, laneIdx) => {
    const cxSrc    = colX[nodeCol.get(from)!]! + Math.floor(colW[nodeCol.get(from)!]! / 2);
    const cxDst    = colX[nodeCol.get(to)!]!   + Math.floor(colW[nodeCol.get(to)!]!   / 2);
    const byBoxSrc = nodeRow.get(from)! * ROW_H + TOP_MARGIN;
    const byBoxDst = nodeRow.get(to)!   * ROW_H + TOP_MARGIN;
    const laneY    = laneIdx;
    const lx = Math.min(cxSrc, cxDst);
    const rx = Math.max(cxSrc, cxDst);
    hLine(lx + 1, rx - 1, laneY, "╌");
    gset(lx, laneY, "┌"); gset(rx, laneY, "┐");
    vLine(laneY + 1, byBoxSrc - 1, cxSrc);
    gset(cxSrc, byBoxSrc, "┴");
    vLine(laneY + 1, byBoxDst - 2, cxDst);
    gset(cxDst, byBoxDst - 1, "▼");
    gset(cxDst, byBoxDst, "┬");
  });

  // Forward-edge connectors
  for (const { from, to } of data.edges) {
    if (backSet.has(`${from}\0${to}`)) continue;
    const sc = nodeCol.get(from)!;
    const sr = nodeRow.get(from)!;
    const tc = nodeCol.get(to)!;
    const tr = nodeRow.get(to)!;
    if (tc <= sc) continue;
    const srcMidY  = sr * ROW_H + 1 + TOP_MARGIN;
    const tgtMidY  = tr * ROW_H + 1 + TOP_MARGIN;
    const gapStart = colX[sc]! + colW[sc]!;
    const gapEnd   = colX[tc]! - 1;
    if (sr === tr) {
      hLine(gapStart, gapEnd - 1, srcMidY);
      gset(gapEnd, srcMidY, "▶");
    } else if (tc === sc + 1) {
      const vx = colX[tc]! - 3;
      if (tr > sr) {
        hLine(gapStart, vx - 1, srcMidY); gset(vx, srcMidY, "┐");
        vLine(srcMidY + 1, tgtMidY - 1, vx); gset(vx, tgtMidY, "└");
        hLine(vx + 1, gapEnd - 1, tgtMidY); gset(gapEnd, tgtMidY, "▶");
      } else {
        hLine(gapStart, vx - 1, srcMidY); gset(vx, srcMidY, "┘");
        vLine(tgtMidY + 1, srcMidY - 1, vx); gset(vx, tgtMidY, "┐");
        hLine(vx + 1, gapEnd - 1, tgtMidY); gset(gapEnd, tgtMidY, "▶");
      }
    }
  }

  const lines = grid.map((row) => row.join("").trimEnd());
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  if (selfLoops.length > 0) {
    lines.push("self-loop" + (selfLoops.length > 1 ? "s" : "") + ": " +
      selfLoops.map(({ from }) => `${from} \u2192 ${from}`).join(", "));
  }
  return lines.join("\n");
}

// ─────────────────────────────────────────────────────────────────────────────
// Vertical layout (rank = row, top → bottom) — the default, mirrors SVG default
// ─────────────────────────────────────────────────────────────────────────────
function buildMaxGraphAsciiVertical(
  nodeOrder: string[],
  data: MaxGraphData,
  backSet: Set<string>,
  rank: Map<string, number>,
  rankGroups: Map<number, string[]>,
  backEdgeList: MaxGraphEdge[],
  selfLoops: MaxGraphEdge[],
): string {
  // row = rank (top→bottom), col = position within rank (left→right)
  const nodeCol = new Map<string, number>();
  const nodeRow = new Map<string, number>();
  for (const [r, ids] of rankGroups) {
    for (let i = 0; i < ids.length; i++) {
      nodeRow.set(ids[i]!, r);
      nodeCol.set(ids[i]!, i);
    }
  }

  const numRanks = rank.size > 0 ? Math.max(...rank.values()) + 1 : 1;
  const numPos   = rankGroups.size > 0
    ? Math.max(...Array.from(rankGroups.values(), (g) => g.length))
    : 1;

  // Width of each "column slot" (position within rank): consistent across all ranks.
  const posW = new Array<number>(numPos).fill(5);
  for (const id of nodeOrder) {
    const p = nodeCol.get(id)!;
    posW[p] = Math.max(posW[p]!, id.length + 4);
  }

  const H_GAP  = 6; // chars between right │ and left │ of adjacent position boxes
  const BOX_H  = 3; // top border + content row + bottom border
  const V_GAP  = 3; // routing rows between box bottom and next rank's box top
  const RANK_H = BOX_H + V_GAP; // = 6 rows per rank level

  const posX = new Array<number>(numPos).fill(0);
  let xAcc = 0;
  for (let p = 0; p < numPos; p++) { posX[p] = xAcc; xAcc += posW[p]! + H_GAP; }
  const totalW = xAcc - H_GAP;

  // Back-edge side lanes — dashed arcs to the right of the grid.
  // Each lane is a vertical column; source exits right, arcs over, dest enters right (◀).
  const numSideLanes  = Math.min(backEdgeList.length, 4);
  const SIDE_LANE_STEP = 4;
  const SIDE_MARGIN   = numSideLanes > 0 ? 2 + numSideLanes * SIDE_LANE_STEP : 0;
  const laneX = (laneIdx: number) => totalW + 2 + laneIdx * SIDE_LANE_STEP;

  // A back-edge arc needs the "gap detour" (drop below the source rank before going right)
  // only when there is at least one sibling node to the RIGHT of the source at the same rank —
  // otherwise a straight horizontal from the content row is fine.
  // For sources at the last rank (no V_GAP below), also add BOTTOM_MARGIN rows.
  const srcNeedsDetour = (from: string): boolean => {
    const rSrc = nodeRow.get(from)!;
    const pSrc = nodeCol.get(from)!;
    return Array.from(nodeCol.entries()).some(([id, p]) => nodeRow.get(id) === rSrc && p > pSrc);
  };
  const needsBottomMargin = backEdgeList.some(
    ({ from }) => nodeRow.get(from)! === numRanks - 1 && srcNeedsDetour(from),
  );
  const BOTTOM_MARGIN = needsBottomMargin ? V_GAP : 0;
  const totalH = (numRanks > 1 ? numRanks * RANK_H - V_GAP : BOX_H) + BOTTOM_MARGIN;
  const gridW  = totalW + SIDE_MARGIN;

  const grid: string[][] = Array.from({ length: totalH }, () => new Array<string>(gridW).fill(" "));
  const gset  = (x: number, y: number, ch: string)             => { if (x >= 0 && x < gridW && y >= 0 && y < totalH) grid[y]![x] = ch; };
  const hLine = (x1: number, x2: number, y: number, ch = "─") => { for (let x = x1; x <= x2; x++) gset(x, y, ch); };
  const vLine = (y1: number, y2: number, x: number, ch = "│") => { for (let y = y1; y <= y2; y++) gset(x, y, ch); };

  // Smart gset: merges box-drawing corner/junction characters so two connectors sharing a cell
  // combine into the correct junction (e.g. │ + └ → ├, keeping both paths visible).
  const CONN: Record<string, number> = {
    "│": 0b1010, "─": 0b0101,
    "┌": 0b1001, "┐": 0b1100, "└": 0b0011, "┘": 0b0110,
    "├": 0b1011, "┤": 0b1110, "┬": 0b1101, "┴": 0b0111,
    "┼": 0b1111,
  };
  const FROM_CONN: Record<number, string> = Object.fromEntries(Object.entries(CONN).map(([k, v]) => [v, k]));
  const jset = (x: number, y: number, ch: string): void => {
    if (x < 0 || x >= gridW || y < 0 || y >= totalH) return;
    const cur = grid[y]![x]!;
    const merged = (CONN[cur] ?? 0) | (CONN[ch] ?? 0);
    grid[y]![x] = merged !== 0 ? (FROM_CONN[merged] ?? ch) : ch;
  };

  // Paint boxes
  for (const id of nodeOrder) {
    const p  = nodeCol.get(id)!;
    const r  = nodeRow.get(id)!;
    const bx = posX[p]!;
    const by = r * RANK_H;
    const w  = posW[p]!;
    const inner = w - 2;
    gset(bx, by, "┌"); hLine(bx + 1, bx + w - 2, by); gset(bx + w - 1, by, "┐");
    gset(bx, by + 1, "│");
    const pad = Math.floor((inner - id.length) / 2);
    for (let i = 0; i < inner; i++) gset(bx + 1 + i, by + 1, " ");
    for (let i = 0; i < id.length; i++) gset(bx + 1 + pad + i, by + 1, id[i]!);
    gset(bx + w - 1, by + 1, "│");
    gset(bx, by + 2, "└"); hLine(bx + 1, bx + w - 2, by + 2); gset(bx + w - 1, by + 2, "┘");
  }

  // Back-edge arcs (to the right).
  //
  // Two routing strategies per arc:
  //
  // SIMPLE (source has no sibling to its right at the same rank):
  //   straight horizontal from source content row → side lane → dashed up → dest.
  //
  // DETOUR (source has a sibling to its right — straight horizontal would cross it):
  //   drop one step right of source → down to belowRankY (routing gap / bottom margin) →
  //   horizontal in that clear row → side lane → dashed up → dest.
  //
  // Process lanes from NEAREST (laneIdx=0) to FURTHEST so that when multiple back-edges
  // share the same source in DETOUR mode, the nearer lane's ┘ at belowRankY is correctly
  // promoted to ┴ (branch junction) by the farther lane's jset hLine.
  const backLanes = backEdgeList.slice(0, numSideLanes);
  for (let laneIdx = 0; laneIdx < backLanes.length; laneIdx++) {
    const { from, to } = backLanes[laneIdx]!;
    const pSrc       = nodeCol.get(from)!;
    const pDst       = nodeCol.get(to)!;
    const rSrc       = nodeRow.get(from)!;
    const rDst       = nodeRow.get(to)!;
    const srcRightX  = posX[pSrc]! + posW[pSrc]! - 1;
    const dstRightX  = posX[pDst]! + posW[pDst]! - 1;
    const srcMidY    = rSrc * RANK_H + 1;      // content row of source box
    const dstMidY    = rDst * RANK_H + 1;      // content row of destination box
    const belowRankY = rSrc * RANK_H + BOX_H;  // first routing row below source rank's boxes
    const lx         = laneX(laneIdx);
    const detour     = srcNeedsDetour(from);

    if (detour) {
      // DETOUR: exit right one step into the gap, drop to belowRankY, then go right to lane.
      const srcExitX = srcRightX + 1; // gap column just right of source box
      jset(srcRightX, srcMidY, "─");  // source right border: │ → ├
      jset(srcExitX, srcMidY, "┐");   // turn: RIGHT → DOWN
      for (let y = srcMidY + 1; y <= belowRankY - 1; y++) jset(srcExitX, y, "│");
      jset(srcExitX, belowRankY, "└"); // turn: DOWN → RIGHT
      for (let x = srcExitX + 1; x <= lx - 1; x++) jset(x, belowRankY, "─");
      // nearest lane gets ┘; farther lanes promote it to ┴ via the jset hLine above
      gset(lx, belowRankY, "┘");
      // dashed vertical lane from belowRankY up to destination
      for (let y = dstMidY + 1; y <= belowRankY - 1; y++) {
        if ((y - dstMidY) % 2 === 1) gset(lx, y, "│");
      }
    } else {
      // SIMPLE: straight horizontal from source content row to lane.
      hLine(srcRightX + 1, lx - 1, srcMidY);
      gset(lx, srcMidY, "┘"); // turn: RIGHT → UP
      // dashed vertical lane from srcMidY up to destination
      for (let y = dstMidY + 1; y <= srcMidY - 1; y++) {
        if ((y - dstMidY) % 2 === 1) gset(lx, y, "│");
      }
    }

    // Lane arrives from BELOW, turns LEFT toward destination.
    gset(lx, dstMidY, "┐");
    if (lx - 1 >= dstRightX + 2) hLine(dstRightX + 2, lx - 1, dstMidY);
    gset(dstRightX + 1, dstMidY, "◀");
  }

  // Forward-edge connectors
  for (const { from, to } of data.edges) {
    if (backSet.has(`${from}\0${to}`)) continue;
    const sp = nodeCol.get(from)!;
    const sr = nodeRow.get(from)!;
    const tp = nodeCol.get(to)!;
    const tr = nodeRow.get(to)!;
    if (tr <= sr) continue; // only draw downward (forward) edges

    const srcBotY  = sr * RANK_H + BOX_H; // first row below source box bottom border
    const dstTopY  = tr * RANK_H;         // top border row of dest box
    const srcMidX  = posX[sp]! + Math.floor(posW[sp]! / 2);
    const dstMidX  = posX[tp]! + Math.floor(posW[tp]! / 2);

    if (sp === tp) {
      // Same column — straight vertical connector
      vLine(srcBotY, dstTopY - 2, srcMidX);
      gset(srcMidX, dstTopY - 1, "▼");
    } else if (tr === sr + 1) {
      // Adjacent rank, different column — L-shaped through the routing gap.
      // Use jset for corners so a shared source column gets a ├/┤ junction instead of overwriting │.
      const routeY = sr * RANK_H + BOX_H + 1; // middle row of the V_GAP routing zone
      if (tp > sp) {
        // going right
        vLine(srcBotY, routeY - 1, srcMidX);
        jset(srcMidX, routeY, "└");
        hLine(srcMidX + 1, dstMidX - 1, routeY);
        jset(dstMidX, routeY, "┐");
        vLine(routeY + 1, dstTopY - 2, dstMidX);
        gset(dstMidX, dstTopY - 1, "▼");
      } else {
        // going left
        vLine(srcBotY, routeY - 1, srcMidX);
        jset(srcMidX, routeY, "┘");
        hLine(dstMidX + 1, srcMidX - 1, routeY);
        jset(dstMidX, routeY, "┐");
        vLine(routeY + 1, dstTopY - 2, dstMidX);
        gset(dstMidX, dstTopY - 1, "▼");
      }
    }
    // Edges spanning more than one rank with a column change are not routed
    // (unusual in typical DAGs where rank = longest-path depth).
  }

  const lines = grid.map((row) => row.join("").trimEnd());
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  if (selfLoops.length > 0) {
    lines.push("self-loop" + (selfLoops.length > 1 ? "s" : "") + ": " +
      selfLoops.map(({ from }) => `${from} \u2192 ${from}`).join(", "));
  }
  return lines.join("\n");
}

export function buildMaxGraphAscii(
  name: string,
  maxGraphs: MaxGraphs,
  orientation: MaxGraphOrientation = "vertical",
): string {
  return `<pre class="npp-max-graph-ascii">${escapeHtml(buildMaxGraphAsciiRaw(name, maxGraphs, orientation))}</pre>`;
}

/** Plain-text ASCII diagram for `name` — suitable for Markdown code fences. */
export function buildMaxGraphAsciiText(
  name: string,
  maxGraphs: MaxGraphs,
  orientation: MaxGraphOrientation = "vertical",
): string {
  return buildMaxGraphAsciiRaw(name, maxGraphs, orientation);
}
